/**
 * Calendar-aware "recency bucket" helpers shared by every list surface
 * that groups rows under Today / Yesterday / Last 7 Days / Last 30 Days /
 * Older headings (agent sidebar, chat sidebar, …).
 *
 * Buckets are anchored to local midnight — "Today" means *this calendar
 * day*, not "within the last 24 hours" — so an item from 11pm yesterday
 * never shows under Today at 9am.
 */

export type TimeBucket = 'today' | 'yesterday' | 'last7' | 'last30' | 'older';

export interface TimeBucketGroup<T> {
  key: TimeBucket;
  label: string;
  items: T[];
}

export const TIME_BUCKET_ORDER: { key: TimeBucket; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'older', label: 'Older' },
];

export function getTimeBucket(timestamp: number, now: Date = new Date()): TimeBucket {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const startOf7DaysAgo = new Date(startOfToday);
  startOf7DaysAgo.setDate(startOf7DaysAgo.getDate() - 7);

  const startOf30DaysAgo = new Date(startOfToday);
  startOf30DaysAgo.setDate(startOf30DaysAgo.getDate() - 30);

  if (timestamp >= startOfToday.getTime()) return 'today';
  if (timestamp >= startOfYesterday.getTime()) return 'yesterday';
  if (timestamp >= startOf7DaysAgo.getTime()) return 'last7';
  if (timestamp >= startOf30DaysAgo.getTime()) return 'last30';
  return 'older';
}

/**
 * The `[from, to)` epoch-ms bounds of a bucket, anchored to the same local
 * midnights {@link getTimeBucket} uses. `from` is the inclusive lower bound
 * (omitted for `older`, which is unbounded below); `to` is the exclusive
 * upper bound (omitted for `today`, which runs up to now). Lets a caller turn
 * a bucket the user acted on — e.g. the sidebar's "Archive all Today" — into a
 * server-side `createdAt` range that covers every matching row, not just the
 * ones a client happens to hold.
 */
export function getTimeBucketBounds(
  bucket: TimeBucket,
  now: Date = new Date(),
): { from?: number; to?: number } {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const startOf7DaysAgo = new Date(startOfToday);
  startOf7DaysAgo.setDate(startOf7DaysAgo.getDate() - 7);

  const startOf30DaysAgo = new Date(startOfToday);
  startOf30DaysAgo.setDate(startOf30DaysAgo.getDate() - 30);

  switch (bucket) {
    case 'today':
      return { from: startOfToday.getTime() };
    case 'yesterday':
      return { from: startOfYesterday.getTime(), to: startOfToday.getTime() };
    case 'last7':
      return { from: startOf7DaysAgo.getTime(), to: startOfYesterday.getTime() };
    case 'last30':
      return { from: startOf30DaysAgo.getTime(), to: startOf7DaysAgo.getTime() };
    case 'older':
      return { to: startOf30DaysAgo.getTime() };
  }
}

/** A plotted bucket's half-open `[startMs, endMs)` span, in epoch ms. */
export interface BucketSpan {
  startMs: number;
  endMs: number;
}

/**
 * The start of the bucket that is still filling up: the one whose span
 * contains `now`. Any chart whose range runs to the present has one, and it
 * holds a fraction of a day of data, so plotting it like a finished bucket
 * reads as a cliff in usage that never happened. Callers hand the result to
 * a chart as its partial bucket so that bucket renders as in progress.
 *
 * Spans are passed in rather than derived from a fixed 24h day because a
 * local calendar day is 23 or 25 hours across a DST switch; the producer of
 * the series already knows the real bounds.
 */
export function inProgressBucket(
  spans: BucketSpan[],
  now: number = Date.now(),
): number | undefined {
  return spans.find((span) => span.startMs <= now && now < span.endMs)?.startMs;
}

/**
 * Group items into time buckets by the timestamp `getTimestamp` returns.
 * Input order is preserved within each bucket, and empty buckets are
 * omitted.
 */
export function groupByTimeBucket<T>(
  items: T[],
  getTimestamp: (item: T) => number,
  now: Date = new Date(),
): TimeBucketGroup<T>[] {
  const buckets: Record<TimeBucket, T[]> = {
    today: [],
    yesterday: [],
    last7: [],
    last30: [],
    older: [],
  };

  for (const item of items) {
    buckets[getTimeBucket(getTimestamp(item), now)].push(item);
  }

  return TIME_BUCKET_ORDER.filter(({ key }) => buckets[key].length > 0).map(({ key, label }) => ({
    key,
    label,
    items: buckets[key],
  }));
}
